/**
 * The DIME identity seam: how a host turns its own session credential into a
 * DIME JWT (the credential every web-api call and gateway-proxy handshake
 * presents).
 *
 * The pluggable unit is the token EXCHANGE, not the sign-in provider —
 * everything downstream of minting (401 re-mint retry, sidecar auth cache,
 * handshake token resolver, connection state machine) only ever consumes
 * minted `DimeIdentity` values. Clerk is one implementation
 * (`createClerkTokenExchange`); a future Paradex/Paradigm exchange (host JWT →
 * DIME JWT via a new web-api /auth endpoint) slots in with zero changes
 * downstream.
 *
 * Never log host tokens or the DIME JWT.
 */

export class DimeApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'DimeApiError';
  }
}

/** A freshly minted DIME JWT plus the routing claims decoded from it. */
export interface DimeIdentity {
  /** The DIME JWT itself (5-minute TTL — use immediately, never store). */
  readonly token: string;
  /** Routes the UI: true ⇒ resolve the connection, false ⇒ access-code screen. */
  readonly onboarded: boolean;
}

/**
 * How this host obtains a DIME JWT. Called immediately before every request
 * that needs one (the JWT's 5-minute TTL means it is never stored or reused
 * across calls beyond the short caches in the client). Implementations throw
 * `DimeApiError` on failure.
 *
 * `apiUrl` is the resolved web-api base (no trailing slash) so an exchange
 * targets the same environment as the client that invokes it.
 */
export type DimeTokenExchange = (apiUrl: string) => Promise<DimeIdentity>;

/**
 * Base64url-decode a JWT's payload segment. Decode only — the backend signed
 * it moments ago over TLS; client-side signature verification adds nothing.
 */
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/gu, '+').replace(/_/gu, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * The Clerk exchange: swap the current Clerk session token for a fresh DIME
 * JWT via POST /v1/auth/identity. `getToken` is Clerk's `useAuth().getToken`
 * — it resolves null when the session has ended.
 */
export function createClerkTokenExchange(
  getToken: () => Promise<string | null>,
): DimeTokenExchange {
  return async (apiUrl) => {
    const clerkToken = await getToken();
    if (clerkToken == null || clerkToken.length === 0) {
      throw new DimeApiError(401, 'No Clerk session token available');
    }
    const res = await fetch(`${apiUrl}/v1/auth/identity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: clerkToken }),
    });
    if (!res.ok) {
      throw new DimeApiError(
        res.status,
        `Identity exchange failed (${res.status})`,
      );
    }
    const body = (await res.json()) as { token?: string };
    if (typeof body.token !== 'string' || body.token.length === 0) {
      throw new DimeApiError(res.status, 'Identity exchange returned no token');
    }
    const payload = decodeJwtPayload(body.token);
    return {
      token: body.token,
      onboarded: payload?.onboarded === true,
    };
  };
}

// FUTURE (endpoint not yet available): createParadexTokenExchange(getParadexJwt)
// — POST the host's Paradex JWT to the upcoming web-api /auth endpoint and
// return the minted DimeIdentity. Intended for the linked-account fast path:
// `DimeCloudApp` tries it silently via `silentExchange` before showing the
// Clerk sign-in, so linked users never round-trip through Clerk.
